import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/store/tenant";
import type { Tenant } from "@/types";
import { getIdpProvider } from "@/types";
import { AlertTriangle, BadgeCheck, AlertCircle, Shield, Globe, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { InputField } from "@/components/ui/InputField";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

interface TenantDetailsTabProps {
  tenant: Tenant;
}

export const TenantDetailsTab: React.FC<TenantDetailsTabProps> = ({ tenant }) => {
  const navigate = useNavigate();
  const tenantStore = useTenant(tenant.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Form states
  const [name, setName] = useState(tenant.name || "");
  const [slug, setSlug] = useState(tenant.slug || "");
  const [description, setDescription] = useState(tenant.description || "");
  const [logoUrl, setLogoUrl] = useState(tenant.logo_url || "");
  const [primaryDomainId, setPrimaryDomainId] = useState(tenant.primary_domain_id || "");
  const [defaultIdpId, setDefaultIdpId] = useState(tenant.default_idp_id || tenant.settings?.default_idp_id || "");
  const [jit, setJit] = useState(tenant.jit || false);

  const { data: domains } = tenantStore.domains;
  const { data: idps, available: availableIdps } = tenantStore.idps;

  const domainOptions = [
    { value: "", label: "Select Primary Domain", icon: <Globe className="w-4 h-4 text-slate-400" /> },
    ...domains.map((d: any) => ({
      value: d.id,
      label: d.domain,
      sublabel: d.is_verified ? "Verified" : "Unverified",
      icon: d.is_verified ? (
        <BadgeCheck className="w-4 h-4 text-emerald-500 fill-emerald-500/10" />
      ) : (
        <AlertCircle className="w-4 h-4 text-slate-400" />
      )
    }))
  ];

  const idpOptions = [
    { value: "", label: "None", icon: <Shield className="w-4 h-4 text-slate-400" /> },
    ...idps.map((ti: any) => {
      const fullIdp = availableIdps?.find((i) => i.id === ti.idp_id);
      const name = fullIdp?.display_name || ti.name || ti.idp_id || ti.id;
      const providerType = fullIdp?.provider || ti.provider;
      const logoUrl = fullIdp?.logo_url || getIdpProvider(providerType || "")?.logoUrl;
      return {
        value: ti.idp_id || ti.id,
        label: name,
        sublabel: providerType ? `Provider: ${providerType}` : undefined,
        logo: logoUrl,
        icon: !logoUrl ? <Shield className="w-4 h-4 text-slate-400" /> : undefined
      };
    })
  ];

  useEffect(() => {
    setName(tenant.name || "");
    setSlug(tenant.slug || "");
    setDescription(tenant.description || "");
    setLogoUrl(tenant.logo_url || "");
    setPrimaryDomainId(tenant.primary_domain_id || "");
    setDefaultIdpId(tenant.default_idp_id || tenant.settings?.default_idp_id || "");
    setJit(tenant.jit || false);
  }, [tenant]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await tenantStore.update({
        name,
        slug,
        description,
        logo_url: logoUrl,
        primary_domain_id: primaryDomainId || undefined,
        default_idp_id: defaultIdpId || undefined,
        jit,
      });
      toast.success("Tenant details updated successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to update tenant details");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleHardDelete = async () => {
    try {
      await tenantStore.hardDelete(tenant.slug);
      navigate("/m/tenants");
    } catch (err: any) {
      // toast is handled by store
    }
  };

  const formattedDate = tenant.created_at ? new Date(tenant.created_at).toLocaleString() : "N/A";

  return (
    <div className="lg:col-span-2 space-y-6">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] border border-slate-200/60 dark:border-slate-800/60 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-[15px] font-bold text-slate-900 dark:text-white">
            General Information
          </h3>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSubmitting}
            className="text-xs py-1.5 px-4 h-auto rounded-full font-semibold"
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Displayname
            </label>
            <InputField
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tenant Name"
              className="w-full text-[13px] py-1.5"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Slug
            </label>
            <InputField
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="tenant-slug"
              className="w-full text-[13px] py-1.5"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Primary Domain
            </label>
            <SearchableSelect
              options={domainOptions}
              value={primaryDomainId}
              onChange={setPrimaryDomainId}
              placeholder="Select Primary Domain"
              hideSelectedSublabel={true}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Default Identity Provider
            </label>
            <SearchableSelect
              options={idpOptions}
              value={defaultIdpId}
              onChange={setDefaultIdpId}
              placeholder="None"
              hideSelectedSublabel={true}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              JIT Provisioning
            </label>
            <div className="flex items-center mt-1">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={jit}
                  onChange={(e) => setJit(e.target.checked)}
                />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                <span className="ml-3 text-[13px] font-medium text-slate-900 dark:text-slate-300">
                  {jit ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Created Date
            </label>
            <p className="text-[13px] font-medium text-slate-900 dark:text-white py-1.5">
              {formattedDate}
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4 pt-6 border-t border-slate-100 dark:border-slate-800/60">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Description
            </label>
            <InputField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tenant description"
              className="w-full text-[13px] py-1.5"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
              Tenant Logo
            </label>
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <div className="h-16 w-16 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0 shadow-sm p-1">
                  <img src={logoUrl} alt="Logo preview" className="w-full h-full object-contain rounded" />
                </div>
              ) : (
                <div className="h-16 w-16 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center shrink-0 text-slate-400">
                  <ImageIcon className="w-5 h-5 mb-0.5 opacity-50" />
                  <span className="text-[10px] text-center leading-tight">No logo</span>
                </div>
              )}
              <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <InputField
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="Logo URL or uploaded image data URL..."
                  className="w-full text-[13px] py-1.5"
                />
                <label className="cursor-pointer bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 flex items-center justify-center text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0">
                  Upload File
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-red-200/60 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 flex items-center gap-2 border-b border-red-100/60 dark:border-red-900/30">
          <AlertTriangle className="w-4.5 h-4.5 text-red-500" />
          <h3 className="font-bold text-red-600 dark:text-red-400 text-[15px]">
            Danger Zone
          </h3>
        </div>
        <div className="px-6 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h4 className="font-bold text-slate-900 dark:text-red-100 text-[13px]">
              Delete this tenant
            </h4>
            <p className="text-xs text-slate-600 dark:text-red-300 mt-1">
              Once you delete a tenant, there is no going back. All data, users, and applications will be permanently removed.
            </p>
          </div>
          {!showDeleteConfirm ? (
            <Button
              variant="danger"
              className="shrink-0 font-bold px-5 bg-red-600 hover:bg-red-700 rounded-full text-[13px]"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Tenant
            </Button>
          ) : (
            <div className="flex flex-col gap-2 shrink-0">
              <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                Type <span className="font-bold">"{tenant.name}"</span> to confirm
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  placeholder={tenant.name}
                />
                <Button
                  variant="danger"
                  className="font-bold px-4 bg-red-600 hover:bg-red-700 rounded-md text-xs h-8 border-0"
                  disabled={deleteConfirmText !== tenant.name}
                  onClick={handleHardDelete}
                >
                  Confirm Delete
                </Button>
                <Button
                  variant="ghost"
                  className="px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-xs h-8 bg-transparent"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
